import { PayloadContent } from './profiles/payload-content.model';

/**
 * Configuration Profile Keys
 * At the top level, a profile property list contains the following keys:
 */
export class ConfigurationProfile {
  /**
   * Gets or sets the unique device identifier(UDID) of the device.
   */
  public udid: string;

  /**
   * Gets or sets optional. Set to true if there is a removal passcode.
   */
  public hasRemovalPasscode?: boolean;

  /**
   * Gets or sets optional. Set to true if the profile is encrypted.
   */
  public isEncrypted?: boolean;

  /**
   * Gets or sets optional. Set to true if this profile was installed by the current MDM service.
   */
  public isManaged?: boolean;

  /**
   * Gets or sets optional. Array of payload dictionaries. Not present if IsEncrypted is true.
   */
  public payloadContent: PayloadContent[];

  /**
   * Gets or sets optional.A description of the profile, shown on the Detail screen for the profile.
   * This should be descriptive enough to help the user decide whether to install the profile.
   */
  public payloadDescription: string;

  /**
   * Gets or sets optional.A human-readable name for the profile.
   * This value is displayed on the Detail screen. It does not have to be unique.
   */
  public payloadDisplayName: string;

  /**
   * Gets or sets optional.A date on which a profile is considered to have expired and can be updated over the air.
   * This key is only used if the profile is delivered via over-the-air profile delivery.
   */
  public payloadExpirationDate?: string;

  /**
   * Gets or sets a reverse-DNS style identifier (com.example.myprofile, for example) that identifies the profile.
   * This string is used to determine whether a new profile should replace an existing one or should be added.
   */
  public payloadIdentifier: string;

  /**
   * Gets or sets optional. A human-readable string containing the name of the organization that provided the profile.
   */
  public payloadOrganization: string;

  /**
   * Gets or sets a globally unique identifier for the profile. The actual content is unimportant, but it must be globally unique.
   * In macOS, you can use uuidgen to generate reasonable UUIDs.
   */
  public payloadUUID: string;

  /**
   * Gets or sets optional. Supervised only.
   * If present and set to true, the user cannot delete the profile (unless the profile has a removal password and the user provides it).
   */
  public payloadRemovalDisallowed?: boolean;

  /**
   * Gets or sets the only supported value is Configuration.
   */
  public payloadType: string;

  /**
   * Gets or sets the version number of the profile format.
   * This describes the version of the configuration profile as a whole, not of the individual profiles within it.
   * Currently, this value should be 1.
   */
  public payloadVersion: number;

  /**
   * Gets or sets optional. Determines if the profile should be installed for the system or the user.
   * In many cases, it determines the location of the certificate items, such as keychains.
   * Though it is not possible to declare different payload scopes, payloads, like VPN,
   * may automatically install their items in both scopes if needed.
   * Legal values are System and User, with User as the default value.
   * Availability: Available in macOS 10.7 and later.
   */
  public payloadScope: string;

  /**
   * Gets or sets optional. The date on which the profile will be automatically removed.
   */
  public removalDate?: string;

  /**
   * Gets or sets optional. Number of seconds until the profile is automatically removed.
   * If the RemovalDate keys is present, whichever field yields the earliest date will be used.
   */
  public durationUntilRemoval?: number;

  /**
   * Gets or sets optional. A dictionary containing these keys and values:
   * For each language in which a consent or license agreement is available,
   * a key consisting of the IETF BCP 47 identifier for that language (for example, en or jp)
   * and a value consisting of the agreement localized to that language.
   * The agreement is displayed in a dialog to which the user must agree before installing the profile.
   * The optional key default with its value consisting of the unlocalized agreement (usually in en).
   * The system chooses a localized version in the order of preference specified by the user(macOS)
   * or based on the user's current language setting(iOS).
   * If no exact match is found, the default localization is used.If there is no default localization,
   * the en localization is used. If there is no en localization, then the first available localization is used.
   * You should provide a default value if possible.
   * No warning will be displayed if the user's locale does not match any localization in the ConsentText dictionary.
   */
  // TODO
  // public consentText: IDictionary<string, string>;
}
